import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/free-solid-svg-icons";
import { callAPI } from "../../../../axios";
import { getUserRoleCodeFromStorage } from "../../../../utils/utils";

const ROLES = [
  { id: "ROLE_SUPER_ADMIN", label: "Super Admin" },
  { id: "ROLE_ADMIN", label: "Admin" },
];

const getNavigationPathByRole = (currentRole) => {
  const roleCode = currentRole;
  if (roleCode === "ROLE_SUPER_ADMIN") {
    return "/hms/Dashboard/Hotels_Dashboard";
  }
  return "/hms/HotelOnboardingWizard";
};

const MyProfile = (props) => {
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [switchRoleLoading, setSwitchRoleLoading] = useState(false);
  const [switchRoleError, setSwitchRoleError] = useState(null);

  const hasSwitchRolePermission = useMemo(() => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const roleCodes = new Set(
        (userDetails?.roles || []).map((role) => role?.roleCode)
      );
      return (
        roleCodes.has("ROLE_ADMIN") && roleCodes.has("ROLE_SUPER_ADMIN")
      );
    } catch {
      return false;
    }
  }, []);

  const currentRoleCode = useMemo(() => {
    return getUserRoleCodeFromStorage() || "ROLE_ADMIN";
  }, []);

  const logout = () => {
    localStorage.clear();
    window.location.replace("/login");
  };

  const toggleRoleDropdown = (e) => {
    e.stopPropagation();
    setIsRoleDropdownOpen((prev) => !prev);
  };

  const onSelectRole = (roleId) => {
    if (roleId === currentRoleCode) {
      setIsRoleDropdownOpen(false);
      return;
    }
    setSwitchRoleError(null);
    setSwitchRoleLoading(true);
    callAPI("/auth/switch-role", "POST", { role: roleId })
      .then((response) => {
        const userDetails = response.userDetails || {};
        const { permissions = [], roles = [] } = userDetails;
        userDetails.availablePermissions = (permissions || []).reduce((acc, item) => {
          if (item?.active) acc.push(item.permissionCode);
          return acc;
        }, []);
        userDetails.availableRoles = (roles || []).reduce((acc, item) => {
          if (item?.active) acc.push(item.roleCode);
          return acc;
        }, []);
        localStorage.setItem("userDetails", JSON.stringify(userDetails));
        if (response.jwt) localStorage.setItem("jwt", response.jwt);
        if (response.currentRole) localStorage.setItem("currentRole", response.currentRole);
        const currentRole = getUserRoleCodeFromStorage();
        setIsRoleDropdownOpen(false);
        window.location.href = getNavigationPathByRole(currentRole);
      })
      .catch((err) => {
        const message = err?.response?.data?.errorMessage || err?.response?.data?.errors?.[0] || "Failed to switch role.";
        setSwitchRoleError(message);
      })
      .finally(() => {
        setSwitchRoleLoading(false);
      });
  };

  return (
    <nav
      ref={props.dropdownRef}
      className={`my-profile ${props.isActive ? "active" : "inactive"}`}
    >
      <div className="my-profile_list">
        <Link to="/hms/myAccount">
          <PersonIcon className="mx-0 pl-0" /> 
          My Account
        </Link>

        {hasSwitchRolePermission && (
          <div className="switch-role-wrapper">
            <div
              className="switch-role switch-role-header"
              onClick={toggleRoleDropdown}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && toggleRoleDropdown(e)
              }
            >
              <SwapHorizIcon className="switch-role-icon" />
              <span>Switch Role</span>
              {isRoleDropdownOpen ? (
                <ExpandLessIcon className="switch-role-caret" />
              ) : (
                <ExpandMoreIcon className="switch-role-caret" />
              )}
            </div>
            {isRoleDropdownOpen && (
              <div className="switch-role-dropdown">
                {switchRoleError && (
                  <p className="switch-role-error text-danger small mb-1 px-2">{switchRoleError}</p>
                )}
                <ul>
                  {ROLES.map((role) => (
                    <li
                      key={role.id}
                      className={currentRoleCode === role.id ? "role-active" : ""}
                      onClick={() => !switchRoleLoading && onSelectRole(role.id)}
                      onKeyDown={(e) => {
                        if (!switchRoleLoading && (e.key === "Enter" || e.key === " ")) {
                          e.preventDefault();
                          onSelectRole(role.id);
                        }
                      }}
                      role="option"
                      aria-selected={currentRoleCode === role.id}
                      aria-busy={switchRoleLoading}
                      tabIndex={0}
                    >
                      {role.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <Link to="/hms/changePassword">
          <FontAwesomeIcon icon={faKey} className="" /> Change Password
        </Link>
        <p onClick={logout}>
          <LogoutIcon className="mx-0 pl-0" /> Logout
        </p>
      </div>
    </nav>
  );
};

export default MyProfile;
